<template>
  <el-dialog
    v-model="dialogVisible"
    title="Signaler un problème"
    width="560px"
    destroy-on-close
    @close="handleClose"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item
        label="Titre"
        prop="title"
      >
        <el-input
          v-model="form.title"
          maxlength="200"
          show-word-limit
          placeholder="Décrivez le problème en une phrase"
        />
      </el-form-item>

      <el-form-item
        label="Description"
        prop="description"
      >
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="5"
          placeholder="Détaillez le problème, les étapes pour le reproduire..."
        />
      </el-form-item>

      <el-form-item label="Pièce jointe">
        <el-upload
          :auto-upload="false"
          :limit="1"
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
          :file-list="fichiers"
          :on-change="choisirFichier"
          :on-remove="retirerFichier"
          :on-exceed="remplacerFichier"
        >
          <el-button size="small">
            Joindre une capture
          </el-button>
          <template #tip>
            <div class="el-upload__tip">
              Une image ou un PDF, {{ tailleMaxMo }} Mo au plus. Une capture vaut souvent
              mieux qu'une description.
            </div>
          </template>
        </el-upload>
      </el-form-item>

      <!-- Options avancées (masquées par défaut, l'IA s'en charge) -->
      <el-collapse-transition>
        <div v-if="showAdvanced">
          <el-divider content-position="left">
            Classification manuelle
          </el-divider>

          <el-form-item label="Type">
            <el-radio-group v-model="form.type">
              <el-radio-button value="">
                Automatique (IA)
              </el-radio-button>
              <el-radio-button value="bug">
                Bug
              </el-radio-button>
              <el-radio-button value="amélioration">
                Amélioration
              </el-radio-button>
              <el-radio-button value="feature">
                Nouvelle fonctionnalité
              </el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="Priorité">
            <el-radio-group v-model="form.priority">
              <el-radio-button value="">
                Automatique (IA)
              </el-radio-button>
              <el-radio-button value="P1-critique">
                P1 : Critique
              </el-radio-button>
              <el-radio-button value="P2-important">
                P2 : Important
              </el-radio-button>
              <el-radio-button value="P3-normal">
                P3 : Normal
              </el-radio-button>
              <el-radio-button value="P4-faible">
                P4 : Faible
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
        </div>
      </el-collapse-transition>

      <div style="text-align: center; margin-top: 4px">
        <el-button
          link
          type="info"
          size="small"
          @click="showAdvanced = !showAdvanced"
        >
          {{ showAdvanced ? 'Masquer les options' : 'Classifier manuellement' }}
        </el-button>
      </div>
    </el-form>

    <template #footer>
      <el-button @click="handleClose">
        Annuler
      </el-button>
      <el-button
        type="primary"
        :loading="loading"
        @click="handleSubmit"
      >
        Envoyer
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { createIssue } from '@/api/gitlab'

// Le serveur refuse au-delà ; l'annoncer ici évite un aller-retour pour rien.
const TAILLE_MAX_MO = 5
import { formatErrorToast } from '@/utils/formHelpers'

const props = defineProps({
  visible: { type: Boolean, default: false },
})
const emit = defineEmits(['update:visible'])

const store = useStore()
const route = useRoute()

const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
})

const formRef = ref(null)
    const loading = ref(false)
    const showAdvanced = ref(false)

    const form = reactive({
      type: '',
      priority: '',
      title: '',
      description: '',
    })

const fichiers = ref([])
const tailleMaxMo = TAILLE_MAX_MO

/**
 * Un seul fichier, refusé tout de suite s'il est trop lourd : le signalement part
 * souvent d'un écran qui ne marche pas, autant ne pas y ajouter un envoi qui rate.
 */
function choisirFichier(fichier) {
  if (fichier.raw && fichier.raw.size > TAILLE_MAX_MO * 1024 * 1024) {
    ElMessage.warning(`Ce fichier dépasse ${TAILLE_MAX_MO} Mo, il n'a pas été joint.`)
    fichiers.value = []

    return
  }

  fichiers.value = [fichier]
}

function retirerFichier() {
  fichiers.value = []
}

// `limit` à 1 fait tomber le second choix dans `on-exceed` plutôt que dans
// `on-change` : sans cela, joindre une autre capture ne ferait rien.
function remplacerFichier(nouveaux) {
  choisirFichier({ raw: nouveaux[0], name: nouveaux[0]?.name })
}

const rules = {
  title: [{ required: true, message: 'Le titre est obligatoire', trigger: 'blur' }],
}

function buildDescription() {
  const userName = `${store.getters.prenom} ${store.getters.name}`
  const lines = []
  if (form.description) {
    lines.push(form.description)
  }
  lines.push('')
  lines.push('---')
  lines.push('**Contexte automatique :**')
  lines.push(`- **Utilisateur** : ${userName}`)
  lines.push(`- **Page** : \`${route.path}\`${route.meta?.title ? ` (${route.meta.title})` : ''}`)
  lines.push(`- **URL** : ${window.location.href}`)
  lines.push(`- **Navigateur** : ${navigator.userAgent}`)
  return lines.join('\n')
}

async function handleSubmit() {
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  loading.value = true
  try {
    const labels = []
    if (form.type) labels.push(form.type)
    if (form.priority) labels.push(form.priority)
    await createIssue({
      title: form.title,
      description: buildDescription(),
      labels,
      // L'écran d'où part le signalement, en clair plutôt que noyé dans le
      // markdown : le tri automatique s'en sert pour situer la demande, ce
      // qu'un texte d'utilisateur ne dit presque jamais.
      page: {
        path: route.path,
        name: route.name ?? null,
        title: route.meta?.title ?? null,
      },
      pieceJointe: fichiers.value[0]?.raw ?? null,
    })

    // La détection de doublon est interne au traitement : elle pose un label
    // sur l'issue pour le tri et ne concerne jamais l'utilisateur.
    ElMessage.success('Votre signalement a bien été envoyé, merci !')
    handleClose()
  } catch (error) {
    ElMessage.error(
      formatErrorToast(error, 'Erreur lors de l\'envoi du signalement. Réessayez plus tard.'),
    )
  } finally {
    loading.value = false
  }
}

function handleClose() {
  emit('update:visible', false)
}

watch(() => props.visible, (val) => {
  if (val) {
    form.type = ''
    form.priority = ''
    form.title = ''
    form.description = ''
    fichiers.value = []
    showAdvanced.value = false
  }
})
</script>
